import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Font } from 'mm-theme-configuration/dist/interfaces';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { WebFontsComponentProps, WebFontsContext } from '../../theming/ThemeProviderWithFonts';
import { getStylesheetByProvider } from './webFonts.utils';
import { WebfontLoader } from './webfontLoader';
import { ActualTypographySizesFontFamiliesMap, TypographySizesFontFamiliesMap } from '../../theming/fontSizeTranslator';

interface LinkProps {
  fontConfiguration: Font;
}

const FontLink: React.FunctionComponent<LinkProps> = ({ fontConfiguration }) => {
  const [rel, setRel] = React.useState('preload');
  React.useEffect(() => {
    setRel('stylesheet');
  }, []);

  return (
    <Helmet>
      <link rel={rel} href={getStylesheetByProvider(fontConfiguration)} as="style" />
    </Helmet>
  );
};

/* eslint-disable-next-line jsx-a11y/anchor-is-valid */
const createFontFamilyLink = (fontConfiguration: Font, index: number) => <FontLink fontConfiguration={fontConfiguration} key={index} />;

const createDefaultFontFamiliesMap = (typographySizesFontFamiliesMap: TypographySizesFontFamiliesMap) => {
  return (Object.keys(typographySizesFontFamiliesMap) as TypographySize[]).reduce((sizesObj: ActualTypographySizesFontFamiliesMap, current) => {
    return {
      ...sizesObj,
      [current]: typographySizesFontFamiliesMap[current].defaultFontFamily,
    };
  }, {} as ActualTypographySizesFontFamiliesMap);
};

const createFontFamiliesMap = (typographySizesFontFamiliesMap: TypographySizesFontFamiliesMap) => {
  return (Object.keys(typographySizesFontFamiliesMap) as TypographySize[]).reduce((sizesObj: ActualTypographySizesFontFamiliesMap, current) => {
    return {
      ...sizesObj,
      [current]: typographySizesFontFamiliesMap[current].fontFamily,
    };
  }, {} as ActualTypographySizesFontFamiliesMap);
};

export const WebFonts: React.FunctionComponent<WebFontsComponentProps> = props => {
  const { fontsConfiguration, typographySizesFontFamiliesMap, children } = props;
  const [webFontsContext, setWebFontsContext] = React.useState(createDefaultFontFamiliesMap(typographySizesFontFamiliesMap));

  React.useEffect(() => {
    new WebfontLoader(fontsConfiguration).observeFontLoading().then(() => setWebFontsContext(createFontFamiliesMap(typographySizesFontFamiliesMap)));
  }, [fontsConfiguration, typographySizesFontFamiliesMap]);

  return (
    <WebFontsContext.Provider value={webFontsContext}>
      {fontsConfiguration.map(createFontFamilyLink)}
      {children}
    </WebFontsContext.Provider>
  );
};
